<template>
  <div class="wo-payment-modal">
    <div class="wo-payment-modal__intro">
      <strong>{{ $t('mollie.work_order_complete.title') }}</strong>
      <p>{{ $t('mollie.work_order_complete.subtitle') }}</p>
      <div v-if="autoInvoice" class="wo-payment-modal__invoice">
        {{ $t('mollie.work_order_complete.invoice_created', { number: autoInvoice.invoice_number }) }}
      </div>
    </div>

    <div v-if="paymentLink?.qr_data_url" class="wo-payment-modal__qr-wrap">
      <img
        :src="paymentLink.qr_data_url"
        :alt="$t('invoices.payment_link.qr_label')"
        class="wo-payment-modal__qr"
      />
      <div v-if="amountDue > 0" class="wo-payment-modal__amount">
        <span class="wo-payment-modal__amount-label">
          {{ $t('mollie.work_order_complete.amount_due') }}
        </span>
        <span class="wo-payment-modal__amount-value">
          {{ formatCurrency(amountDue, currency) }}
        </span>
      </div>
      <QrScanHint />
    </div>

    <OnSiteReceipt v-if="hasReceipt" :invoice="autoInvoice" />

    <p v-if="paymentLink?.expires_at" class="wo-payment-modal__expires">
      {{ $t('invoices.payment_link.expires_at', { date: formatDateTime(paymentLink.expires_at) }) }}
    </p>

    <div class="wo-payment-modal__actions">
      <Btn variant="outlined" size="md" @click="handleClose">
        {{ $t('mollie.work_order_complete.close') }}
      </Btn>
      <Btn
        v-if="paymentLink?.checkout_url"
        variant="primary"
        size="md"
        icon="external-link"
        @click="openCheckout"
      >
        {{ $t('mollie.work_order_complete.open_link') }}
      </Btn>
    </div>
  </div>
</template>

<script>
  import { computed, getCurrentInstance } from 'vue';
  import dayjs from 'dayjs';

  import Btn from '@/components/partials/Btn.vue';
  import QrScanHint from '@/components/partials/QrScanHint.vue';
  import OnSiteReceipt from '@/components/invoices/OnSiteReceipt.vue';
  import { formatCurrency } from '@/utils/currency';
  import { resolveAmountDue } from '@/utils/invoiceAmounts';

  export default {
    name: 'WorkOrderPaymentModal',

    components: { Btn, QrScanHint, OnSiteReceipt },

    props: {
      autoInvoice: { type: Object, default: null },
      paymentLink: { type: Object, default: null },
      onClose: { type: Function, default: () => {} },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const amountDue = computed(() => resolveAmountDue(props.autoInvoice));
      const currency = computed(() => props.autoInvoice?.currency || 'EUR');

      // Older payloads (or a failed items lookup) only carry the total; in
      // that case show the amount hero but skip the receipt.
      const hasReceipt = computed(
        () => Array.isArray(props.autoInvoice?.items) && props.autoInvoice.items.length > 0
      );

      const handleClose = () => props.onClose?.();

      const openCheckout = () => {
        const url = props.paymentLink?.checkout_url;
        if (!url) return;
        window.open(url, '_blank', 'noopener,noreferrer');
      };

      const formatDateTime = (date) =>
        date ? dayjs(date).format('DD-MM-YYYY HH:mm') : '';

      return {
        amountDue,
        currency,
        hasReceipt,
        handleClose,
        openCheckout,
        formatDateTime,
        formatCurrency,
        $t,
      };
    },
  };
</script>

<style scoped lang="scss">
  .wo-payment-modal {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
    text-align: center;

    &__intro {
      strong {
        display: block;
        font-size: 1rem;
        font-weight: 600;
        color: var(--slate-900);
      }

      p {
        margin: 0.25rem 0 0;
        font-size: 0.875rem;
        color: var(--slate-600);
        line-height: 1.45;
      }
    }

    &__invoice {
      margin-top: 0.5rem;
      display: inline-block;
      padding: 0.25rem 0.625rem;
      font-size: 0.75rem;
      font-weight: 600;
      background: var(--slate-100);
      color: var(--slate-700);
      border-radius: 0.375rem;
    }

    &__qr-wrap {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.875rem;
      padding: 1rem;
      background: var(--slate-50);
      border-radius: 0.75rem;
      border: 1px solid var(--slate-200);
    }

    &__qr {
      width: 260px;
      max-width: 100%;
      height: auto;
      background: white;
      padding: 0.5rem;
      border-radius: 0.5rem;
    }

    &__amount {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.125rem;
    }

    &__amount-label {
      font-size: 0.75rem;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--slate-500);
    }

    &__amount-value {
      font-size: 1.75rem;
      font-weight: 700;
      line-height: 1.1;
      color: var(--slate-900);
      font-variant-numeric: tabular-nums;
    }

    &__expires {
      margin: 0;
      font-size: 0.75rem;
      color: var(--slate-500);
    }

    &__actions {
      display: flex;
      gap: 0.5rem;
      justify-content: center;
      flex-wrap: wrap;

      @include lg-down {
        flex-direction: column;
        align-items: stretch;
      }
    }
  }
</style>
